import {NgModule, inject} from '@angular/core';
import {Routes, RouterModule, Router} from '@angular/router';
import {CompaniesService} from './services/companies.service';
import {BrowserUtils} from '@azure/msal-browser';
import {HomeComponent} from './home/home.component';
import {FailedComponent} from './failed/failed.component';
import {MsalGuard} from '@azure/msal-angular';
import {organizationAccessGuard} from './guards/organization-access.guard';
import {notCorporateViewerGuard} from './guards/not-corporate-viewer.guard';
import {permissionsAdminGuard} from './guards/permissions-admin.guard';
import {pagePermissionGuard} from './guards/page-permission.guard';
import {unsavedChangesGuard} from './guards/unsaved-changes.guard';
import {customersMsalGuard} from './auth/msal/guards/customers-msal.guard';
import {NotificationsPageComponent} from './notifications/notifications-page/notifications-page.component';
import {provideAuditModule} from './audit-module/audit.providers';
import {provideCustodisModule} from './custodis-module/custodis.providers';
import {environment} from '../environments/environment';

const routes: Routes = [
  {
    path: '',
    component: HomeComponent,
    canActivate: [MsalGuard]
  },
  {
    path: 'onboarding/company',
    loadComponent: () => import('./onboarding-company/onboarding-company.component')
      .then(x => x.OnboardingCompanyComponent),
    canActivate: [MsalGuard, organizationAccessGuard],
    // data: {
    //   permissions: {
    //     only: ['OnboardingAdmin']
    //   }
    // }
  },
  {
    path: 'settings/user',
    loadComponent: () => import('./user-settings/user-settings-wrapper.component')
      .then(x => x.UserSettingsWrapperComponent),
    canActivate: [MsalGuard, organizationAccessGuard]
  },
  {
    path: 'settings/organization',
    loadComponent: () => import('./organization-settings/organization-settings.component')
      .then(x => x.OrganizationSettingsComponent),
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    data: { pageKey: 'corporate.organization-settings' }
  },
  {
    path: 'settings/permissions',
    loadComponent: () => import('./permissions/admin/permissions-admin-page.component')
      .then(x => x.PermissionsAdminPageComponent),
    canActivate: [MsalGuard, organizationAccessGuard, permissionsAdminGuard],
  },
  // {
  //   path: 'settings/company/:companyId',
  //   component: CompanyManagementComponent,
  //   canActivate: [MsalGuard, ngxPermissionsGuard, organizationAccessGuard],
  //   data: {
  //     permissions: {
  //       only: ['OnboardingAdmin']
  //     }
  //   }
  // },
  {
    path: 'settings/company/:companyId',
    pathMatch: 'full',
    loadComponent: () => import('./organization-settings/organization-company-settings/organization-settings-wrapper.component')
      .then((x) => x.OrganizationSettingsComponent),
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    data: { pageKey: 'corporate.company-settings' }
  },
  {
    path: 'settings/callback/:companyId',
    pathMatch: 'full',
    loadComponent: () => import('./organization-settings/organization-company-settings/organization-settings-create-wrapper')
      .then((x) => x.SettingsCreateWrapperComponent),
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    data: { pageKey: 'corporate.company-settings-callback' }
  },
  {
    path: 'create-form/2',
    canActivate: [MsalGuard, organizationAccessGuard],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () => import('./corporate/introducer-create/introducer-create-wrapper.component')
      .then(x => x.IntroducerCreateWrapperComponent),
  },
  {
    path: 'create-form/:id',
    loadComponent: () => import('./corporate/contact/contact-wrapper.component')
      .then(x => x.ContactWrapperComponent),
    pathMatch: 'full',
    canActivate: [MsalGuard, organizationAccessGuard],
    canDeactivate: [unsavedChangesGuard],
  },

  {
    path: 'hr',
    loadComponent: () => import('./hr/hr-dashboard/hr-dashboard.component')
      .then(m => m.HrDashboardComponent),
    canActivate: [MsalGuard]
  },
  {
    path: 'hr/salary-details',
    loadComponent: () => import('./hr/hr-salary-details-page/hr-salary-details-page.component')
      .then(m => m.HrSalaryDetailsPageComponent),
    canActivate: [MsalGuard]
  },
  // HR module — company-level sections (moved out of the per-employee dashboard). Each section
  // is wrapped in a shell that shows the group's items in a left sidebar for quick switching.
  // Components are reused from the employee-dashboard folder.
  {
    path: 'hr/leave',
    loadComponent: () => import('./hr/hr-section-shell/hr-section-shell.component').then(m => m.HrSectionShellComponent),
    canActivate: [MsalGuard],
    data: {
      title: 'Leave Administration',
      icon: 'admin_panel_settings',
      menu: [
        { label: 'Pending Approval', icon: 'pending_actions', route: '/hr/leave/pending' },
        { label: 'All Employee Leaves', icon: 'list_alt', route: '/hr/leave/all' },
        { label: 'Leaves Overview', icon: 'view_list', route: '/hr/leave/overview' },
        { label: 'Team Calendar', icon: 'calendar_month', route: '/hr/leave/calendar' },
        { label: 'Mandatory Leaves', icon: 'event_busy', route: '/hr/leave/holidays' }
      ]
    },
    children: [
      { path: '', redirectTo: 'pending', pathMatch: 'full' },
      { path: 'pending', loadComponent: () => import('./hr/employee-dashboard/components/pending-leaves/pending-leaves.component').then(m => m.PendingLeavesComponent) },
      { path: 'all', loadComponent: () => import('./hr/employee-dashboard/components/all-employee-leaves/all-employee-leaves.component').then(m => m.AllEmployeeLeavesComponent) },
      { path: 'overview', loadComponent: () => import('./hr/employee-dashboard/components/all-employees-leave-overview/all-employees-leave-overview.component').then(m => m.AllEmployeesLeaveOverviewComponent) },
      { path: 'calendar', loadComponent: () => import('./hr/employee-dashboard/components/all-employees-leave-calendar/all-employees-leave-calendar.component').then(m => m.AllEmployeesLeaveCalendarComponent) },
      { path: 'holidays', loadComponent: () => import('./hr/employee-dashboard/components/holiday-management/holiday-management.component').then(m => m.HolidayManagementComponent) }
    ]
  },
  {
    path: 'hr/organization',
    loadComponent: () => import('./hr/hr-section-shell/hr-section-shell.component').then(m => m.HrSectionShellComponent),
    canActivate: [MsalGuard],
    data: {
      title: 'Organization',
      icon: 'business',
      menu: [
        { label: 'Positions', icon: 'work', route: '/hr/organization/positions' },
        { label: 'Assignments', icon: 'assignment', route: '/hr/organization/assignments' },
        { label: 'Salaries', icon: 'euro', route: '/hr/organization/salaries' },
        { label: 'Departments', icon: 'account_tree', route: '/hr/organization/departments' },
        { label: 'Organization Chart', icon: 'schema', route: '/hr/organization/org-chart' }
      ]
    },
    children: [
      { path: '', redirectTo: 'positions', pathMatch: 'full' },
      { path: 'positions', loadComponent: () => import('./hr/employee-dashboard/components/positions-overview/positions-overview.component').then(m => m.PositionsOverviewComponent) },
      { path: 'positions/new', loadComponent: () => import('./hr/employee-dashboard/components/position-detail/position-detail.component').then(m => m.PositionDetailComponent) },
      { path: 'positions/edit/:id', loadComponent: () => import('./hr/employee-dashboard/components/position-detail/position-detail.component').then(m => m.PositionDetailComponent) },
      { path: 'positions/view/:id', loadComponent: () => import('./hr/employee-dashboard/components/position-detail/position-detail.component').then(m => m.PositionDetailComponent) },
      { path: 'assignments', loadComponent: () => import('./hr/employee-dashboard/components/assignments-overview/assignments-overview.component').then(m => m.AssignmentsOverviewComponent) },
      { path: 'assignments/new', loadComponent: () => import('./hr/employee-dashboard/components/assignment-detail/assignment-detail.component').then(m => m.AssignmentDetailComponent) },
      { path: 'assignments/edit/:id', loadComponent: () => import('./hr/employee-dashboard/components/assignment-detail/assignment-detail.component').then(m => m.AssignmentDetailComponent) },
      { path: 'assignments/view/:id', loadComponent: () => import('./hr/employee-dashboard/components/assignment-detail/assignment-detail.component').then(m => m.AssignmentDetailComponent) },
      { path: 'salaries', loadComponent: () => import('./hr/employee-dashboard/components/salaries-overview/salaries-overview.component').then(m => m.SalariesOverviewComponent) },
      { path: 'salaries/new', loadComponent: () => import('./hr/employee-dashboard/components/salary-detail/salary-detail.component').then(m => m.SalaryDetailComponent) },
      { path: 'salaries/edit/:employeeId/:salaryId', loadComponent: () => import('./hr/employee-dashboard/components/salary-detail/salary-detail.component').then(m => m.SalaryDetailComponent) },
      { path: 'salaries/view/:employeeId/:salaryId', loadComponent: () => import('./hr/employee-dashboard/components/salary-detail/salary-detail.component').then(m => m.SalaryDetailComponent) },
      { path: 'departments', loadComponent: () => import('./hr/employee-dashboard/components/departments-overview/departments-overview.component').then(m => m.DepartmentsOverviewComponent) },
      { path: 'org-chart', loadComponent: () => import('./hr/employee-dashboard/components/org-chart/org-chart.component').then(m => m.OrgChartComponent) }
    ]
  },

  {
    path: 'employee/:id',
    loadChildren: () => import('./hr/employee-dashboard/employee-dashboard-routing.module').then(m => m.EmployeeDashboardRoutingModule)
  },

  {
    path: 'forms-table',
    loadComponent: () => import('./corporate/form-table/form-table-wrapper.component')
      .then(x => x.FormTableWrapperComponent),
    canActivate: [MsalGuard, organizationAccessGuard],
  },
  {
    path: 'potential-clients',
    loadComponent: () => import('./corporate/potential-clients/potential-clients.component')
      .then(x => x.PotentialClientsComponent),
    canActivate: [MsalGuard, organizationAccessGuard],
  },
  {
    path: 'duplicate-clients',
    loadComponent: () => import('./corporate/duplicate-clients/duplicate-clients.component')
      .then(x => x.DuplicateClientsComponent),
    canActivate: [MsalGuard, organizationAccessGuard],
  },
  {
    // "New from the portal": clients coming in from the client onboarding portal.
    path: 'portal-applications',
    loadComponent: () => import('./corporate/portal-applications/portal-applications.component')
      .then(x => x.PortalApplicationsComponent),
    canActivate: [MsalGuard, organizationAccessGuard],
  },
  {
    path: 'edit-introducer/:id',
    canActivate: [MsalGuard, organizationAccessGuard],
    canDeactivate: [unsavedChangesGuard],
    loadComponent: () => import('./corporate/introducer-create/introducer-create-wrapper.component')
      .then(x => x.IntroducerCreateWrapperComponent),
  },
  {
    path: 'edit-form/:id',
    loadComponent: () => import('./corporate/edit-form/edit-form-wrapper.component')
      .then(x => x.EditFormWrapperComponent),
    canActivate: [MsalGuard, organizationAccessGuard],
    canDeactivate: [unsavedChangesGuard]
  },
  {
    path: 'form-constructor',
    loadComponent: () => import('./corporate/form-constructor/form-constructor-wrapper.component')
      .then(x => x.FormConstructorWrapperComponent),
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { pageKey: 'corporate.form-constructor' }
  },
  {
    path: 'corporate/risk-settings',
    loadComponent: () => import('./compliance/risk-assessment-settings/risk-assessment-settings-wrapper.component')
      .then(x => x.RiskAssessmentSettingsWrapperComponent),
    canActivate: [MsalGuard, pagePermissionGuard],
    data: { pageKey: 'corporate.risk-settings' }
  },
  {
    path: 'risk-assessment/answers/:formId/:submittedFormId/:id/:businessId',
    loadComponent: () => import('./corporate/risk-assesment/components/risk-stepper/risk-stepper.component')
      .then(x => x.RiskStepperComponent),
    pathMatch: 'full',
  },
  {
    path: 'paf-Document',
    loadComponent: () => import('./corporate/paf-document/paf-document.component')
      .then(x => x.PafDocumentComponent),
    pathMatch: 'full',
  },

  {
    path: 'time-sheet',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./time-sheet/time-sheet-wrapper.component')
      .then(x => x.TimeSheetWrapperComponent)
  },
  {
    path: 'time-sheet-reports',
    // Open to everyone: non-admins get client reports only, limited to their own entries.
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./reporting/time-sheet-reports-page/time-sheet-reports-page.component')
      .then(x => x.TimeSheetReportsPageComponent)
  },
  {
    path: 'profitability-report',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () => import('./reporting/profitability-report/profitability-report.component')
      .then(x => x.ProfitabilityReportComponent),
    data: { pageKey: 'corporate.profitability-report' }
  },
  {
    path: 'log-changes',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () => import('./admin/log-changes/log-changes.component')
      .then(x => x.LogChangesComponent),
    data: { pageKey: 'corporate.log-changes' }
  },
  {
    path: 'admin/pending-changes',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () => import('./admin/pending-changes/pending-changes-page.component')
      .then(x => x.PendingChangesPageComponent),
    data: { pageKey: 'corporate.pending-changes' }
  },
  {
    path: 'audit-logs',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () => import('./admin/audit-logs/audit-logs.component')
      .then(x => x.AuditLogsComponent),
    data: { pageKey: 'corporate.audit-logs' }
  },
  {
    // Klidi exchange staff tools (KYC review, Scrypt, crypto gates), linked from the Klidi report's Exchange
    // Administration section. Klidi.Api decides who may use them ([AdminAuthorize]); the pages show its refusal.
    path: 'klidi-admin',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./corporate/klidi-admin/klidi-admin.routes').then(x => x.KLIDI_ADMIN_ROUTES)
  },
  {
    // Monthly time & cost report — the email's "Open full report" lands here with ?period=yyyy-MM&org=.
    // The API is admin-only (salary-derived figures); the page explains that to anyone else.
    // Listed before 'reports' so that route doesn't swallow it.
    path: 'reports/time-cost',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./time-sheet/time-sheet-monthly-report/time-sheet-monthly-report.component')
      .then(x => x.TimeSheetMonthlyReportComponent)
  },
  {
    path: 'reports',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () => import('./reporting/corporate-reports/corporate-reports.component')
      .then(x => x.CorporateReportsComponent),
    data: { pageKey: 'corporate.reports' }
  },

  {
    path: 'invoicing',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./invoicing-module/invoicing-module-routing/invoicing.module').then(x => x.InvoicingModule)
  },
  // ─── Inventory module ───────────────────────────────────────────────────────────────────
  // Ported from BC_Inventory_UI (itself a fork of this app). Its components were already
  // standalone, so the two NgModule routing wrappers were dropped in favour of the plain
  // `Routes` array they wrapped.
  {
    path: 'inventory',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./inventory-module/inventory-module-routing/inventory-module-routes').then(x => x.inventoryModuleRoutes)
  },
  {
    path: 'global-requests',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./global-requests/global-requests.module').then(x => x.GlobalRequestsModule)
  },
  {
    // Unsaved forms from every module (formAutosave). Each user sees only their own.
    path: 'drafts',
    canActivate: [MsalGuard],
    loadComponent: () => import('./shared/form-drafts/my-form-drafts/my-form-drafts.component').then(x => x.MyFormDraftsComponent)
  },
  // ─── Accounting module ──────────────────────────────────────────────────────────────────
  // Ported from the standalone BC_Accounting_UI app and converted to standalone components.
  // Brings its own sidenav shell (styled to BC_UI's design system) and lazy-loads the
  // bank / crypto / finance sub-trees beneath it. Authentication is BC_UI's MsalGuard —
  // the module's own MSAL bootstrap was dropped on import.
  {
    path: 'accounting',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./accounting-module/accounting/accounting.routes').then(x => x.ACCOUNTING_ROUTES)
  },
  {
    path: 'invoicing-dashboard',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./invoicing-module/components/dashboard/invoicing-dashboard.component')
      .then(x => x.InvoicingDashboardComponent),
  },
  {
    path: 'pluton-invoicing',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./pluton-invoicing-module/invoicing-module-routing/pluton-invoicing.module').then(x => x.PlutonInvoicingModule)
  },
  {
    path: 'pluton-invoicing-dashboard',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./pluton-invoicing-module/components/dashboard/invoicing-dashboard.component')
      .then(x => x.PlutonInvoicingDashboardComponent),
  },
  {
    path: 'login-failed',
    component: FailedComponent
  },
  {
    // Public, unauthenticated landing page — customers look up a submitted form
    // with their reference number + unique code. No guards.
    path: 'form-lookup',
    loadComponent: () => import('./forms-lookup/forms-lookup.component')
      .then(x => x.FormsLookupComponent),
    pathMatch: 'full',
  },
  {
    path: 'important-dates',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./important-dates/important-dates.component')
      .then(x => x.ImportantDatesComponent),
  },
  {
    path: 'auth/form/:id',
    canActivate: [customersMsalGuard],
    loadComponent: () => import('./auth/auth-onboard-form/auth-onboard-form.component')
      .then(x => x.AuthOnboardFormComponent),
    pathMatch: 'full'
  },
  {
    path: 'client-referral-agreement',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/client-referral-agreement/client-referral-agreement.component')
      .then(x => x.ClientReferralAgreementComponent),
  },
  {
    path: 'nda-agreement',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/nda-agreement/nda-agreement.component')
      .then(x => x.NdaAgreementComponent),
  },
  {
    path: 'engagement-agreement',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/engagement-agreement/engagement-agreement.component')
      .then(x => x.EngagementAgreementComponent),
  },
  {
    path: 'ariadni-cyl1',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/ariadni-cyl1/ariadni-cyl1.component')
      .then(x => x.AriadniCyl1Component),
  },
  {
    path: 'e-codes-application',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/e-codes-application/e-codes-application.component')
      .then(x => x.ECodesApplicationComponent),
  },
  {
    path: 'auth-representatives',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/auth-representatives/auth-representatives.component')
      .then(x => x.AuthRepresentativesComponent),
  },
  {
    path: 'authorization-form',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/authorization-form/authorization-form.component')
      .then(x => x.AuthorizationFormComponent),
  },
  {
    path: 'm121-english',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/m121-english/m121-english.component')
      .then(x => x.M121EnglishComponent),
  },
  {
    path: 'm121-greek',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/m121-greek/m121-greek.component')
      .then(x => x.M121GreekComponent),
  },
  {
    path: 'dividends-certificate',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/documents/dividends-certificate/dividends-certificate.component')
      .then(x => x.DividendsCertificateComponent),
  },
  {
    path: 'file-system',
    // The DMS is an everyday tool for all staff, not an admin report, so it is gated on module
    // access only — same as Planner / Phonebook / Reference Numbers. It previously carried
    // pagePermissionGuard, which denied anyone without an explicit role row and let only
    // CorporateAdmin through the legacy bridge, so ordinary users were bounced to home even
    // though the header still showed the DMS tab.
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () =>  import('./file-storage/file-storage.component')
      .then(x => x.FileStorageComponent),
  },
  {
    path: 'share-form',
    canActivate: [MsalGuard, pagePermissionGuard, organizationAccessGuard],
    loadComponent: () =>  import('./sharing-forms/components/sharing-forms-table/sharing-forms-table.component')
      .then(x => x.SharingFormsTableComponent),
    data: { pageKey: 'corporate.share-form' }
  },
  {
    path: 'task-planner',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () =>  import('./corporate/user-planer/user-planer.component')
      .then(x => x.UserPlanerComponent),
    // data: {
    //   permissions: {
    //     only: ['CorporateAdmin']
    //   }
    // }
  },
  {
    path: 'reference-numbers',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/reference-numbers/reference-numbers.component')
      .then(x => x.ReferenceNumbersComponent),
  },
  {
    path: 'company-shares',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/company-shares/company-shares.component')
      .then(x => x.CompanySharesComponent),
  },
  {
    path: 'phonebook',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./corporate/phonebook/components/phonebook-page/phonebook-page.component')
      .then((x) => x.PhonebookPageComponent)
  },
  {
    // Retired: a note opens on the tagged-notes page. Kept as a redirect for the links already out
    // there (stored notification links, search results from an older API, bookmarks). The old page
    // took the clicked message as `noteId` and its thread as `rootMeetingNoteId`; tagged-notes wants
    // the thread as `noteId` and the message as `targetNoteId`.
    path: 'meeting-note',
    pathMatch: 'full',
    redirectTo: ({queryParams}) => {
      const root = queryParams['rootMeetingNoteId'] || queryParams['noteId'];
      const target = queryParams['targetNoteId']
        || (queryParams['rootMeetingNoteId'] && queryParams['noteId'] !== queryParams['rootMeetingNoteId']
          ? queryParams['noteId'] : undefined);
      return inject(Router).createUrlTree(['/tagged-notes'], {
        queryParams: {
          ...(root ? {noteId: root} : {}),
          ...(queryParams['formId'] ? {formId: queryParams['formId']} : {}),
          ...(target ? {targetNoteId: target} : {}),
          ...(queryParams['org'] ? {org: queryParams['org']} : {}),
        },
      });
    },
  },
  {
    // Internal share links: a DMS document opened for a signed-in colleague (see the share dialog).
    path: 'documents/:id',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./file-storage/components/document-link-page/document-link-page.component')
      .then((x) => x.DocumentLinkPageComponent)
  },
  {
    path: 'tagged-notes',
    canActivate: [MsalGuard, organizationAccessGuard, notCorporateViewerGuard],
    loadComponent: () => import('./corporate/meeting-notes/tagged-notes-page/tagged-notes-page.component')
      .then((x) => x.TaggedNotesPageComponent)
  },
  {
    path: 'auth/success',
    canActivate: [customersMsalGuard],
    loadComponent: () => import('./auth/auth-success/auth-success.component')
      .then(x => x.AuthSuccessComponent)
  },
  {
    path: 'notifications',
    component: NotificationsPageComponent,
    canActivate: [MsalGuard, organizationAccessGuard]
  },
  {
    // The gear in the notification dropdown has always navigated here (navigateToNotificationSettings),
    // but the route did not exist, so it was a dead link.
    path: 'settings/notifications',
    loadComponent: () => import('./notifications/notification-settings/notification-settings.component')
      .then(x => x.NotificationSettingsComponent),
    canActivate: [MsalGuard, organizationAccessGuard]
  },

  // Shared landing for modules shown in the switcher but not built yet (routed with ?module=<Name>).
  {
    path: 'coming-soon',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadComponent: () => import('./coming-soon/coming-soon.component')
      .then(x => x.ComingSoonComponent)
  },

  // ─── Tax module ─────────────────────────────────────────────────────────────────────────
  // BC_Tax_Mod's UI, ported into src/app/tax-module. Its own routes lived under /accounting,
  // which this app already gives to the accounting module, so the workspace is re-based on /tax
  // and absorbs the returns / settings screens that were already registered here.
  //
  // Tax is still shown as "coming soon" in the switcher, but the workspace is reachable by direct
  // URL so we can keep building it. The pagePermissionGuard is intentionally omitted for now —
  // restore it (with `pageKey`) once the Tax permission/license ships, so end-users are gated again.
  {
    path: 'tax',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./tax-module/tax-module-routing/tax-module-routes').then(x => x.taxModuleRoutes)
  },

  // ─── Signatures module ──────────────────────────────────────────────────────────────────
  // Replaces WiseStamp for the group. The self-service screens (/signatures/me) are open to any
  // authenticated user — everyone in the group has a signature — while the admin screens carry
  // pageKeys and are gated by pagePermissionGuard on SignaturesAdmin.
  //
  // Its API (bc-signatures-api) is specified in backend-todo-signatures.md and is not deployed
  // yet, so the screens render their loading/error states against a 404 until it ships.
  {
    path: 'signatures',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./signatures/signatures-routing/signature-routes').then(x => x.signatureRoutes)
  },

  // ─── Migration module ───────────────────────────────────────────────────────────────────
  // BC_Migration_Mod's UI (client/bct-migration-client), ported into src/app/migration-module.
  // Its components were already standalone, so the app bootstrap, shell, login screen and JWT
  // auth stack were dropped: MsalGuard signs the user in and MsalCustomerInterceptor attaches the
  // token, which BCT.Migration.API validates against the same B2C employee app registration.
  // Section access inside the workspace (reports / administration) is gated by the module's own
  // role guards — see migration-module/guards/migration-role.guard.ts.
  {
    path: 'migration',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./migration-module/migration-module-routing/migration-module-routes')
      .then(x => x.migrationModuleRoutes)
  },

  // ─── Audit module ───────────────────────────────────────────────────────────────────────
  // BC_Audit_UI's audit-module, ported into src/app/audit-module unchanged — it was written as a
  // drop-in (NgModule-based, `standalone: false`, imports nothing outside its own folder) so the
  // host app only has to mount it, hand it the API base URLs and register its stylesheet.
  //
  // API endpoints arrive through the AUDIT_MODULE_CONFIG token instead of the module reading
  // `environment` itself — BC_Audit_UI's environment shape is flat, ours is nested under
  // apiConfig, and the token keeps the two projects in sync without edits on either side.
  //
  // The switcher tile is `available` and gated on the AuditUser permission (modules.service.ts),
  // which admins grant from the module-access drawer like any other module. Note the screens still
  // render AuditDataService's prototype data — AuditApiService is wired to the real endpoints but
  // no screen calls it yet. pagePermissionGuard is intentionally omitted, as it is on Tax: restore
  // it (with a `pageKey`) once Audit page permissions are registered.
  {
    path: 'audit',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./audit-module/audit.module').then(x => x.AuditModule),
    providers: provideAuditModule({
      identityApiUrl: environment.apiConfig.IdentityAPI.endpoint,
      corporateApiUrl: environment.apiConfig.corporateAPI.endpoint,
      invoicingApiUrl: environment.apiConfig.legacyInvoicingApi.endpoint
    })
  },

  // ─── Custodis vault ─────────────────────────────────────────────────────────────────────
  // Custodis_UI's screens, ported into src/app/custodis-module on the audit-module pattern:
  // NgModule-based, `standalone: false`, and importing nothing outside its own folder — API
  // base URLs arrive through CUSTODIS_MODULE_CONFIG rather than the module reading `environment`.
  //
  // The standalone app's whole auth layer (AuthService, AuthInterceptor, authGuard,
  // TenantContextService) was dropped on import. MsalCustomerInterceptor already issues a token
  // with the audience Custodis validates — the SAME audience as IdentityAPI's scopes — and stamps
  // X-BC-Tenant, which is what the standalone app could not do on localhost: with no ?org= it
  // never bootstrapped a tenant, so the API rejected every call with
  // "Token missing required identity claims." Adding custodisAPI to isModuleApi() is what makes
  // that header flow, so keep the two in step.
  //
  // Organisations & staff come from BC_Identity (IdentityOrgService), not Custodis's own
  // /organisations endpoints — BC_UI already owns that list and a second copy would diverge.
  {
    path: 'custodis',
    canActivate: [MsalGuard, organizationAccessGuard],
    loadChildren: () => import('./custodis-module/custodis.module').then(x => x.CustodisModule),
    providers: provideCustodisModule(
      {
        custodisApiUrl: environment.apiConfig.custodisAPI.endpoint,
        identityApiUrl: environment.apiConfig.IdentityAPI.endpoint,
        corporateApiUrl: environment.apiConfig.corporateAPI.endpoint
      },
      // Resolve the service once (this runs in an injection context), then hand the module a
      // getter it can call at any time. Read lazily rather than captured as a number: the tab's
      // company is per-tab state in OrgScopeStore and can change under the module.
      () => {
        const companies = inject(CompaniesService);
        return () => companies.managedCompanyId || null;
      }
    )
  },

  // ─── Mobile shell ───────────────────────────────────────────────────────────────────────
  // The old phone app lived under /m. Phones now get the same app as everyone, laid out for their
  // screen (docs/redesign/mobile), so /m only forwards old links and installed home-screen icons
  // to the matching page.
  {
    path: 'm',
    children: [
      { path: '', pathMatch: 'full', redirectTo: '/' },
      { path: 'notifications', redirectTo: '/notifications' },
      { path: 'approvals', redirectTo: '/notifications' },
      { path: 'clients', redirectTo: '/forms-table?category=clients' },
      { path: 'notes', redirectTo: '/tagged-notes' },
      { path: '**', redirectTo: '/' },
    ],
  },

  {
    path: '**',
    redirectTo: '/'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    // Don't perform initial navigation in iframes or popups
    initialNavigation: !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup() ? 'enabledNonBlocking' : 'disabled' // Set to enabledBlocking to use Angular Universal
  })],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
